import React, {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useRef,
  useState,
} from "react";
import { toast } from "react-toastify";
import { RegulationContext } from "../../../App";
import {
  ANOMALY_DETECT_MAX_POLL_RETRIES,
  ANOMALY_DETECT_POLLING_INTERVAL,
  ANOMALY_DETECT_STORAGE_KEY,
  ANOMALY_LAST_DETECT_KEY,
} from "../../../core/constants/common";
import AnomalyService from "../../../core/service/AnomalyService";
import { resolveAnomalyRegulationId } from "./anomalyRegulationUtils";

const TERMINAL_STATUSES = new Set(["COMPLETED", "FAILED"]);
const ACTIVE_STATUSES = new Set(["PENDING", "RUNNING"]);

const INITIAL_JOB = {
  id: null,
  status: null,
  progressPct: 0,
  totalRules: null,
  processedRules: 0,
  startedAt: null,
  completedAt: null,
  errorMessage: null,
  isPolling: false,
  pollError: null,
  lastCompletedAt: null,
  period: null,
  monthLabel: null,
};

const normalizeDetectStatus = (status) => {
  const normalized = status ? String(status).trim().toUpperCase() : null;
  if (!normalized) return null;
  if (normalized === "IN_PROGRESS") return "RUNNING";
  return normalized;
};

const mapJobDto = (dto, extras = {}) => ({
  id: dto.id ?? null,
  status: normalizeDetectStatus(dto.status),
  progressPct: dto.progressPct ?? 0,
  totalRules: dto.totalRules ?? null,
  processedRules: dto.processedRules ?? 0,
  startedAt: dto.startedAt ?? null,
  completedAt: dto.completedAt ?? null,
  errorMessage: dto.errorMessage ?? null,
  ...extras,
});

const readStoredJob = () => {
  try {
    const raw = sessionStorage.getItem(ANOMALY_DETECT_STORAGE_KEY);
    return raw ? JSON.parse(raw) : null;
  } catch {
    return null;
  }
};

const writeStoredJob = (job) => {
  if (!job?.id || TERMINAL_STATUSES.has(job.status)) {
    sessionStorage.removeItem(ANOMALY_DETECT_STORAGE_KEY);
    return;
  }
  sessionStorage.setItem(
    ANOMALY_DETECT_STORAGE_KEY,
    JSON.stringify({
      id: job.id,
      status: job.status,
      progressPct: job.progressPct,
      totalRules: job.totalRules,
      processedRules: job.processedRules,
      startedAt: job.startedAt,
      completedAt: job.completedAt,
      errorMessage: job.errorMessage,
      period: job.period,
      monthLabel: job.monthLabel,
    })
  );
};

const readLastRun = () => {
  try {
    const raw = localStorage.getItem(ANOMALY_LAST_DETECT_KEY);
    return raw ? JSON.parse(raw) : null;
  } catch {
    return null;
  }
};

const writeLastRun = (info) => {
  try {
    localStorage.setItem(ANOMALY_LAST_DETECT_KEY, JSON.stringify(info));
  } catch {
    // ignore storage errors
  }
};

const AnomalyDetectionContext = createContext(null);

export const AnomalyDetectionProvider = ({ children, t }) => {
  const { glbRegulation } = useContext(RegulationContext);
  const [job, setJob] = useState(INITIAL_JOB);
  const [lastRun, setLastRun] = useState(() => readLastRun());
  const pollerRef = useRef(null);
  const pollRetryRef = useRef(0);
  const jobRef = useRef(job);

  useEffect(() => {
    jobRef.current = job;
  }, [job]);

  const clearPollTimer = useCallback(() => {
    if (pollerRef.current) {
      clearTimeout(pollerRef.current);
      pollerRef.current = null;
    }
  }, []);

  const stopPolling = useCallback(() => {
    clearPollTimer();
    setJob((prev) => ({ ...prev, isPolling: false }));
  }, [clearPollTimer]);

  const handleTerminalJob = useCallback(
    (nextJob) => {
      stopPolling();
      pollRetryRef.current = 0;
      writeStoredJob(nextJob);

      if (nextJob.status === "COMPLETED") {
        const seconds =
          nextJob.startedAt && nextJob.completedAt
            ? Math.max(
                1,
                Math.round(
                  (new Date(nextJob.completedAt) - new Date(nextJob.startedAt)) / 1000
                )
              )
            : null;

        const runRecord = {
          period: nextJob.period ?? null,
          monthLabel: nextJob.monthLabel ?? null,
          completedAt: nextJob.completedAt ?? new Date().toISOString(),
          seconds,
          processedRules: nextJob.processedRules ?? null,
          totalRules: nextJob.totalRules ?? null,
        };
        writeLastRun(runRecord);
        setLastRun(runRecord);

        setJob((prev) => ({
          ...INITIAL_JOB,
          lastCompletedAt: Date.now(),
        }));
        if (t) {
          toast.success(t("AnomalyDetection.detectCompleted.label"), {
            position: toast.POSITION.TOP_RIGHT,
          });
        }
      } else if (nextJob.status === "FAILED") {
        setJob((prev) => ({
          ...prev,
          ...nextJob,
          isPolling: false,
          pollError: null,
        }));
      }
    },
    [stopPolling, t]
  );

  const startPolling = useCallback(() => {
    clearPollTimer();
    pollRetryRef.current = 0;
    setJob((prev) => ({ ...prev, isPolling: true, pollError: null }));

    const runPoll = () => {
      const current = jobRef.current;
      if (!current.id || !ACTIVE_STATUSES.has(current.status)) {
        stopPolling();
        return;
      }

      AnomalyService.getDetectStatus(current.id)
        .then((res) => {
          pollRetryRef.current = 0;
          const dto = res.data || {};
          const nextJob = mapJobDto(dto, {
            period: current.period,
            monthLabel: current.monthLabel,
            isPolling: !TERMINAL_STATUSES.has(dto.status),
            pollError: null,
            lastCompletedAt: current.lastCompletedAt,
          });

          if (TERMINAL_STATUSES.has(dto.status)) {
            handleTerminalJob(nextJob);
          } else {
            setJob(nextJob);
            writeStoredJob(nextJob);
            pollerRef.current = setTimeout(runPoll, ANOMALY_DETECT_POLLING_INTERVAL);
          }
        })
        .catch(() => {
          pollRetryRef.current += 1;
          if (pollRetryRef.current >= ANOMALY_DETECT_MAX_POLL_RETRIES) {
            stopPolling();
            writeStoredJob(null);
            setJob((prev) => ({
              ...prev,
              isPolling: false,
              pollError: t
                ? t("AnomalyDetection.detectPollError.label")
                : "Unable to check detection status. Please try again.",
            }));
          } else {
            pollerRef.current = setTimeout(runPoll, ANOMALY_DETECT_POLLING_INTERVAL);
          }
        });
    };

    runPoll();
  }, [clearPollTimer, handleTerminalJob, stopPolling, t]);

  const applyJobFromDto = useCallback(
    (dto, extras = {}) => {
      const normalizedStatus = normalizeDetectStatus(dto.status);
      const effectiveStatus = normalizedStatus || (dto.id ? "PENDING" : null);
      const shouldPoll = !!dto.id && ACTIVE_STATUSES.has(effectiveStatus);

      const nextJob = mapJobDto({ ...dto, status: effectiveStatus }, {
        ...extras,
        isPolling: shouldPoll,
        pollError: null,
      });
      setJob((prev) => ({ ...prev, ...nextJob }));
      writeStoredJob(nextJob);

      if (TERMINAL_STATUSES.has(nextJob.status)) {
        handleTerminalJob(nextJob);
      } else if (shouldPoll) {
        jobRef.current = { ...jobRef.current, ...nextJob };
        startPolling();
      }
      return nextJob;
    },
    [handleTerminalJob, startPolling]
  );

  const startDetect = useCallback(
    (period, monthLabel) => {
      const regulationId = resolveAnomalyRegulationId(glbRegulation);
      return AnomalyService.runDetect(regulationId, period).then((res) => {
        const dto = res.data || {};
        applyJobFromDto(dto, { period, monthLabel });
        return dto;
      });
    },
    [applyJobFromDto, glbRegulation]
  );

  const startBackfill = useCallback(
    (payload, monthLabel) => {
      const regulationId = resolveAnomalyRegulationId(glbRegulation);
      return AnomalyService.runDetectBackfill(regulationId, payload).then((res) => {
        const dto = res.data || {};
        applyJobFromDto(dto, {
          period: payload?.period ?? null,
          monthLabel,
        });
        return dto;
      });
    },
    [applyJobFromDto, glbRegulation]
  );

  const clearJob = useCallback(() => {
    stopPolling();
    pollRetryRef.current = 0;
    writeStoredJob(null);
    setJob((prev) => ({
      ...INITIAL_JOB,
      lastCompletedAt: prev.lastCompletedAt,
    }));
  }, [stopPolling]);

  useEffect(() => {
    const stored = readStoredJob();
    const storedJobId = stored?.id ?? stored?.jobId;
    if (!storedJobId) return;

    const restored = {
      ...stored,
      id: storedJobId,
      isPolling: ACTIVE_STATUSES.has(stored.status),
      pollError: null,
      lastCompletedAt: null,
    };
    jobRef.current = { ...jobRef.current, ...restored };
    setJob((prev) => ({ ...prev, ...restored }));

    AnomalyService.getDetectStatus(storedJobId)
      .then((res) => {
        const dto = res.data || {};
        applyJobFromDto(dto, {
          period: stored.period,
          monthLabel: stored.monthLabel,
        });
      })
      .catch(() => {
        sessionStorage.removeItem(ANOMALY_DETECT_STORAGE_KEY);
      });
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => () => stopPolling(), [stopPolling]);

  const isJobActive = ACTIVE_STATUSES.has(job.status);

  const value = useMemo(
    () => ({
      ...job,
      isJobActive,
      lastRun,
      startDetect,
      startBackfill,
      clearJob,
    }),
    [job, isJobActive, lastRun, startDetect, startBackfill, clearJob]
  );

  return (
    <AnomalyDetectionContext.Provider value={value}>
      {children}
    </AnomalyDetectionContext.Provider>
  );
};

export const useAnomalyDetection = () => {
  const ctx = useContext(AnomalyDetectionContext);
  if (!ctx) {
    throw new Error("useAnomalyDetection must be used within AnomalyDetectionProvider");
  }
  return ctx;
};

export default AnomalyDetectionContext;
